import { Injectable } from '@angular/core';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { environment } from 'src/environments/environment';
import { SecurityService } from '../shared/security/security.service';

export interface ChatMessageCallbacks {
  onTextMessageReceived: (message: CometChat.TextMessage) => void;
  onMediaMessageReceived?: (message: CometChat.MediaMessage) => void;
  onError?: (error: CometChat.CometChatException) => void;
}

@Injectable({
  providedIn: 'root',
})
export class CometChatService {
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private loginPromise: Promise<CometChat.User | null> | null = null;
  private lastAuthToken: string | null = null;

  constructor(
    private securityService: SecurityService,
  ) {}

  init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.doInit();
    }
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    if (this.initialized) { return; }

    const { appId, region } = environment.cometchat;
    if (!appId) {
      console.warn('[CometChat] appId not configured. Skipping initialization.');
      return;
    }

    const appSettings = new CometChat.AppSettingsBuilder()
      .subscribePresenceForAllUsers()
      .setRegion(region)
      .build();

    try {
      await CometChat.init(appId, appSettings);
      this.initialized = true;
      console.log('[CometChat] initialized successfully');
    } catch (e) {
      console.error('[CometChat] initialization error:', e);
    }
  }

  async login(authToken: string): Promise<CometChat.User | null> {
    if (!authToken) {
      console.warn('[CometChat] authToken not provided. Skipping login.');
      return null;
    }

    await this.init();

    if (this.loginPromise && this.lastAuthToken === authToken) {
      return this.loginPromise;
    }

    this.lastAuthToken = authToken;
    this.loginPromise = this.performLogin(authToken);
    return this.loginPromise;
  }

  async ensureAuthenticated(): Promise<CometChat.User | null> {
    await this.init();

    const loggedInUser = await CometChat.getLoggedinUser();
    if (loggedInUser) { return loggedInUser; }

    if (this.loginPromise) {
      const pendingUser = await this.loginPromise.catch(() => null);
      if (pendingUser) { return pendingUser; }

      const resolvedUser = await CometChat.getLoggedinUser();
      if (resolvedUser) { return resolvedUser; }
    }

    const authToken = this.securityService.credential?.cometChatAuthToken;
    if (!authToken) { return null; }

    return this.login(authToken);
  }

  async logout(): Promise<void> {
    try {
      await CometChat.logout();
      console.log('[CometChat] logout successful');
    } catch (e) {
      console.warn('[CometChat] logout error (may already be logged out):', e);
    }
  }

  async sendMessage(
    receiverId: string,
    text: string,
    receiverType: 'GROUP' | 'USER' = 'GROUP',
  ): Promise<CometChat.TextMessage> {
    const user = await this.ensureAuthenticated();
    if (!user) { throw new Error('COMETCHAT_AUTH_REQUIRED'); }
    const message = new CometChat.TextMessage(
      receiverId,
      text,
      receiverType === 'USER' ? CometChat.RECEIVER_TYPE.USER : CometChat.RECEIVER_TYPE.GROUP,
    );
    return CometChat.sendMessage(message) as Promise<CometChat.TextMessage>;
  }

  async fetchHistory(
    receiverId: string,
    receiverType: 'GROUP' | 'USER' = 'GROUP',
    limit = 50,
  ): Promise<CometChat.BaseMessage[]> {
    const user = await this.ensureAuthenticated();
    if (!user) { return []; }

    const builder = new CometChat.MessagesRequestBuilder().setLimit(limit);
    const request = (receiverType === 'USER'
      ? builder.setUID(receiverId)
      : builder.setGUID(receiverId)
    ).build();

    return request.fetchPrevious();
  }

  async getMyUid(): Promise<string> {
    await this.init();
    try {
      const user = await CometChat.getLoggedinUser();
      return user?.getUid() ?? '';
    } catch {
      return '';
    }
  }

  private async performLogin(authToken: string): Promise<CometChat.User | null> {
    try {
      const user = await CometChat.login(authToken) as CometChat.User;
      console.log('[CometChat] login successful');
      return user;
    } catch (e: any) {
      const alreadyLoggedIn = e?.code === 'ERROR_ALREADY_LOGGED_IN' || e?.code === 'USER_ALREADY_LOGGED_IN';
      if (alreadyLoggedIn) { return CometChat.getLoggedinUser(); }
      console.error('[CometChat] login error:', e);
      throw e;
    } finally {
      this.loginPromise = null;
    }
  }

  addMessageListener(key: string, callbacks: ChatMessageCallbacks): void {
    CometChat.addMessageListener(
      key,
      new CometChat.MessageListener({
        onTextMessageReceived: callbacks.onTextMessageReceived,
        onMediaMessageReceived: callbacks.onMediaMessageReceived ?? (() => {}),
        onError: callbacks.onError ?? ((e: any) => console.error('[CometChat] listener error:', e)),
      }),
    );
  }

  removeMessageListener(key: string): void {
    CometChat.removeMessageListener(key);
  }
}
